import React from 'react';
import { useNavigate } from 'react-router-dom';
import AudienceFilters from '../components/AudienceFilters';
import AudienceTable from '../components/AudienceTable';
import { Btn, Card, Failed, Note, PageBand, SectionHead, T } from '../components/ui';
import { useEmailManager } from '../index';

/**
 * `/admin/email` — where the Email Manager opens.
 *
 * The design's landing frame is the **audience**: who the platform can reach, filtered, counted and
 * exportable, under a header whose three actions are the rest of the navigation — Settings, View
 * Templates, and Create Campaign. So that is what this page is, and the audience is no longer a
 * page an operator reaches by starting a campaign they may not intend to send.
 *
 * The campaign list is no longer the second card here. It is on Setup & Automation, beside the
 * scheduler, because that is the panel that changes it: an operator schedules a send and then wants
 * to see it queued, and answering that meant navigating back to this page. What is left is one
 * question — who the platform can reach — which is what this frame is.
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const {
    filters,
    filtersWereSet,
    setFilters,
    audience,
    audienceLoading,
    facets,
    events,
    loadAudience,
    exportCsv,
    exporting,
    exportError,
    failed
  } = useEmailManager();

  /*
    The filters travel with the navigation.

    They used to live only in the shell's state, so this link carried nothing — and a reload on the
    other side left the composer reporting "every eligible fan". They are in the address bar now,
    which is what makes the link shareable and survivable rather than merely convenient.
  */
  const createCampaign = () =>
    navigate({
      pathname: '/admin/email/setup',
      search: filtersWereSet ? `?filters=${encodeURIComponent(JSON.stringify(filters))}` : ''
    });

  return (
    <>
      <PageBand
        brand
        title="Email Manager Dashboard"
        subtitle="Manage templates, audiences, campaigns, and scheduled sends"
        /*
          Four actions, where the design gives three.

          `Reports` is the addition, and it is not decoration: the board has no frame that links to
          the analytics, the tab strip that used to has been removed, and this is the page every
          other one comes back to. Without a button here that route exists only if someone types
          it.
        */
        actions={
          <>
            <Btn variant="outline" icon="Gear" onClick={() => navigate('/admin/email/settings')}>
              Settings
            </Btn>
            <Btn variant="outline" icon="Chart" onClick={() => navigate('/admin/email/reports')}>
              Reports
            </Btn>
            <Btn variant="outline" icon="Mail" onClick={() => navigate('/admin/email/templates')}>
              View Templates
            </Btn>
            <Btn
              icon="Plus"
              onClick={createCampaign}
              title={
                filtersWereSet
                  ? 'Opens the setup page with these filters already applied.'
                  : 'Opens the setup page. No audience is set yet, so choose the filters here first.'
              }
            >
              Create Campaign
            </Btn>
          </>
        }
      />

      <Card id="audience">
        <SectionHead
          icon="Users"
          title="Users & Audience Filters"
          /*
            Export sits on the card the list is in, where the design puts it — and where an operator
            is looking when they press it. Its refusal is anchored below for the same reason: a
            failed export used to be silent, the button un-dimming while either a file appeared or
            it did not.
          */
          right={
            <Btn variant="outline" icon="Download" onClick={exportCsv} disabled={exporting}>
              {exporting ? 'Preparing…' : 'Export Filtered List (CSV)'}
            </Btn>
          }
        />

        {exportError ? <Note style={{ color: T.red }}>{exportError}</Note> : null}

        <AudienceFilters
          filters={filters}
          onChange={setFilters}
          total={audience.total}
          audienceTotal={audience.audienceTotal}
          loading={audienceLoading}
          countries={facets.countries}
          teams={facets.teams}
          events={events}
        />

        {/*
          A failed audience query said nothing at all.

          `markLoad('audience', …)` has recorded the outcome since the load states went in, and no
          page read it — so a failed query rendered the table's empty state, and "0 of 12,482 fans"
          under filters that had in fact never been answered. That is the one wrong answer this
          panel can give: it is the number an operator decides a send on.
        */}
        {failed('audience') ? (
          <Failed title="The audience could not be counted" onRetry={() => loadAudience()}>
            These filters were not answered, so the count above is not a result. Nothing about who
            this campaign would reach can be read from it.
          </Failed>
        ) : (
          <AudienceTable
            rows={audience.rows}
            sort={filters.sort}
            onSortChange={(sort) => setFilters({ ...filters, sort })}
            onLoadMore={() => loadAudience(audience.nextCursor)}
            hasMore={!!audience.nextCursor}
            loading={audienceLoading}
          />
        )}

        <Note>
          These filters carry through to Create Campaign, and a campaign stores the filters rather
          than the list — it re-resolves them at send time, so a fan who unsubscribes in between is
          not mailed. The count here is therefore an estimate by construction.
        </Note>
      </Card>
    </>
  );
};

export default Dashboard;
